/** The tour's collections as draft records (plan §3, D7): each row is a record of its
 *  own (`alias:12`, `tagcat:3`, `pretrip:9`; a negative id is a row the draft creates)
 *  and the tour record holds the collection's membership list (`additional_custom_ids.
 *  order` — row ids in order).
 *
 *  A collection travels whole (an omitted row means delete — compile rule 4): the
 *  membership list's order, each row from the server's raw row with its entries
 *  overlaid, new rows without an `id` and filled from `defaults`. The rows' fields come
 *  into the editor with their phases (P2 aliases and tag categories, P3 pre-trip items);
 *  this module is their shape, so baselines, compile, drift and 400 mapping already
 *  handle them. */
import type { AdditionalCustomId, PreTripItemRow, TagCategoryM2M, TourDetail } from '@/api/tours';
import { membershipPaths } from '@/features/records/collections/membership';
import type { CollectionSpec as RecordCollectionSpec } from '@/features/records/collections/spec';
import { ALL_FLAGS, type Flag } from '@/features/tag-categories/associations/flags';

import { DAYS_PATH, ITINERARY_LISTS } from '../itinerary/model';

export type CollectionKey = 'additional_custom_ids' | 'tag_categories' | 'pre_trip_items';

type Raw = Record<string, unknown> & { id: number };

/** A tour collection (the records `CollectionSpec` over the tour's detail). */
export type CollectionSpec = RecordCollectionSpec<TourDetail, CollectionKey>;

const ref = (id: unknown) => (id === null || id === undefined ? null : { id });
const refs = (ids: unknown) => (Array.isArray(ids) ? ids.map((id) => ({ id })) : []);
const ids = (xs: unknown) =>
  Array.isArray(xs) ? (xs as Array<{ id: number }>).map((x) => x.id).sort((a, b) => a - b) : [];

/** Strip the entity prefix of a row path (`alias.custom_id` → `custom_id`). */
function bare(entity: string, values: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const prefix = `${entity}.`;
  for (const [path, v] of Object.entries(values)) if (path.startsWith(prefix)) out[path.slice(prefix.length)] = v;
  return out;
}

function prefixed(entity: string, values: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(values).map(([k, v]) => [`${entity}.${k}`, v]));
}

function withoutUndefined(o: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined));
}

const ALIASES_ORDER = 'additional_custom_ids.order';
const TAG_CATEGORIES_ORDER = 'tag_categories.order';
const PRE_TRIP_ITEMS_ORDER = 'pre_trip_items.order';

/** Every membership-list path of the tour record (its collections' and the itinerary's
 *  lists): merged on a refetch, never drifted; their new members drive `nextTempId`. */
export const MEMBERSHIP_PATHS = membershipPaths([ALIASES_ORDER, TAG_CATEGORIES_ORDER, PRE_TRIP_ITEMS_ORDER], ITINERARY_LISTS);

// ---------------------------------------------------------------------------
// Aliases (additional external IDs)
// ---------------------------------------------------------------------------

/** An alias's scalar fields (draft paths `alias.<f>`). */
export const ALIAS_FIELDS = [
  'custom_id',
  'id_type',
  'use_for_matching',
  'api_queryable',
  'integration',
  'restech_account',
  'comment',
  'ext_link',
  'managed',
] as const;
/** Who keeps an alias (`alias.managed`): true = an import (read-only here until taken
 *  over), false = ops. */
export const ALIAS_MANAGED = 'alias.managed';
/** An alias's id sets (draft values: sorted ids; on the wire `[{id}]`). */
export const ALIAS_ID_SETS = ['tour_user_tags', 'targets'] as const;
/** Read-only parts of a stored alias row, never sent back. */
const ALIAS_READ_ONLY = ['used_by'] as const;

const aliases: CollectionSpec = {
  key: 'additional_custom_ids',
  entity: 'alias',
  membership: ALIASES_ORDER,
  memberships: MEMBERSHIP_PATHS,
  rows: (d) => (d.additional_custom_ids ?? []) as unknown as Raw[],
  project: (row) => {
    const r = row as unknown as AdditionalCustomId;
    return prefixed('alias', {
      ...Object.fromEntries(ALIAS_FIELDS.map((f) => [f, r[f] ?? null])),
      managed: r.managed === true,
      ...Object.fromEntries(ALIAS_ID_SETS.map((f) => [f, ids(r[f])])),
    });
  },
  toWire: (raw, values) => {
    const v = bare('alias', values);
    const out: Record<string, unknown> = { ...(raw ?? {}), ...v };
    for (const k of ALIAS_READ_ONLY) delete out[k];
    for (const k of ALIAS_ID_SETS) out[k] = k in v ? refs(v[k]) : refs(ids(raw?.[k]));
    // `managed: false` on a stored row TAKES IT OVER from its import, so it is sent only
    // as the server told it, or as the draft set it: a read without the flag never
    // turns into a take-over; `true` (hand back) always travels. A new row is always
    // created ops-owned — the flag is not sent.
    if (!raw || (typeof raw.managed !== 'boolean' && out.managed !== true)) delete out.managed;
    out.id = raw ? raw.id : undefined;
    return withoutUndefined(out);
  },
  defaults: prefixed('alias', {
    custom_id: '',
    id_type: 'product_id',
    use_for_matching: true,
    api_queryable: true,
    integration: null,
    restech_account: null,
    comment: null,
    ext_link: null,
    managed: false,
    tour_user_tags: [],
    targets: [],
  }),
  rowErrorPath: 'alias.custom_id',
};

// ---------------------------------------------------------------------------
// Tag categories (the tour's own rows)
// ---------------------------------------------------------------------------

/** The shared flags (`tag-categories/associations/flags.ts`) a tour's own row carries in
 *  the editor. NOT `departure_key`: the console tour serializer does not expose it, so the
 *  editor would read it as off and send `departure_key: false` on every tour PATCH —
 *  wiping the stored value (catalog.test.ts pins this). */
export type TourTagcatFlag = Exclude<Flag, 'departure_key'>;

/** Every boolean of a row the editor edits (18). */
export const TAGCAT_BOOLEANS: readonly TourTagcatFlag[] = ALL_FLAGS.filter(
  (f): f is TourTagcatFlag => f !== 'departure_key',
);

const tagCategories: CollectionSpec = {
  key: 'tag_categories',
  entity: 'tagcat',
  membership: TAG_CATEGORIES_ORDER,
  memberships: MEMBERSHIP_PATHS,
  rows: (d) => (d.tag_categories_own ?? []) as unknown as Raw[],
  project: (row) => {
    const r = row as unknown as TagCategoryM2M;
    return prefixed('tagcat', {
      category: r.category?.id ?? null,
      min_tags: r.min_tags,
      max_tags: r.max_tags,
      ...Object.fromEntries(TAGCAT_BOOLEANS.map((f) => [f, Boolean(r[f])])),
    });
  },
  toWire: (raw, values) => {
    const v = bare('tagcat', values);
    const out: Record<string, unknown> = { ...(raw ?? {}), ...v };
    if ('category' in v) out.category = ref(v.category);
    else if (raw && typeof raw.category === 'object' && raw.category !== null) {
      out.category = ref((raw.category as { id: number }).id);
    }
    delete out.group_tour;
    delete out.sequence; // the list order is the sequence (the funnel numbers it)
    out.id = raw ? raw.id : undefined;
    return withoutUndefined(out);
  },
  defaults: prefixed('tagcat', {
    category: null,
    min_tags: 0,
    max_tags: 0,
    ...Object.fromEntries(TAGCAT_BOOLEANS.map((f) => [f, false])),
  }),
  rowErrorPath: 'tagcat.category',
};

// ---------------------------------------------------------------------------
// Pre-trip items
// ---------------------------------------------------------------------------

const preTripItems: CollectionSpec = {
  key: 'pre_trip_items',
  entity: 'pretrip',
  membership: PRE_TRIP_ITEMS_ORDER,
  memberships: MEMBERSHIP_PATHS,
  rows: (d) => (d.pre_trip_items ?? []) as unknown as Raw[],
  project: (row) => {
    const r = row as unknown as PreTripItemRow;
    return prefixed('pretrip', {
      item: r.item?.id ?? null,
      required: r.required,
      all_users: r.all_users,
      all_travelers: r.all_travelers,
      roles: ids(r.roles),
      agroups: ids(r.agroups),
      users: ids(r.users),
    });
  },
  toWire: (raw, values) => {
    const v = bare('pretrip', values);
    const base = raw as (Raw & Partial<PreTripItemRow>) | null;
    return withoutUndefined({
      id: base ? base.id : undefined,
      item: ref('item' in v ? v.item : (base?.item?.id ?? null)),
      required: 'required' in v ? v.required : base?.required,
      all_users: 'all_users' in v ? v.all_users : base?.all_users,
      all_travelers: 'all_travelers' in v ? v.all_travelers : base?.all_travelers,
      roles: refs('roles' in v ? v.roles : ids(base?.roles)),
      users: refs('users' in v ? v.users : ids(base?.users)),
      agroups: refs('agroups' in v ? v.agroups : ids(base?.agroups)),
    });
  },
  defaults: prefixed('pretrip', {
    item: null,
    required: false,
    all_users: true,
    all_travelers: false,
    roles: [],
    agroups: [],
    users: [],
  }),
  rowErrorPath: 'pretrip.item',
  // The server's pre-trip row messages name their subject (item, role, group, user).
  rowErrorLabel: null,
};

export const COLLECTIONS: readonly CollectionSpec[] = [aliases, tagCategories, preTripItems];

export const COLLECTION_BY_ENTITY: ReadonlyMap<string, CollectionSpec> = new Map(COLLECTIONS.map((c) => [c.entity, c]));
export const COLLECTION_BY_MEMBERSHIP: ReadonlyMap<string, CollectionSpec> = new Map(
  COLLECTIONS.map((c) => [c.membership, c]),
);

/** The itinerary's membership list (day ids, on the tour record). The days' own lists —
 *  a day's stops (`day.slots`), a stop's activity rows (`slot.activities`) — are
 *  membership lists of those records (`itinerary/model.ts`); the tree compiles whole. */
export const ITINERARY_DAYS = DAYS_PATH;
